import { useState, useEffect, useCallback } from 'react';
import notificacionService from '../services/notificacionService';

import { log } from '../utils/logger';
/**
 * Hook para gestionar notificaciones
 */
export const useNotificaciones = (autoFetch = true) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtener notificaciones
   */
  const fetchNotificaciones = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificacionService.obtenerNotificaciones(params);
      setNotificaciones(response.data);
      setNoLeidas(response.noLeidas || 0);
      return response;
    } catch (err) {
      setError(err.message);
      log.error('Error al obtener notificaciones:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtener contador de no leídas
   */
  const fetchContadorNoLeidas = useCallback(async () => {
    try {
      const response = await notificacionService.contarNoLeidas();
      setNoLeidas(response.count || 0);
      return response.count;
    } catch (err) {
      log.error('Error al obtener contador de notificaciones:', err);
    }
  }, []);

  /**
   * Marcar como leída
   */
  const marcarComoLeida = useCallback(async (id) => {
    try {
      await notificacionService.marcarComoLeida(id);
      
      // Actualizar estado local
      setNotificaciones(prev => 
        prev.map(notif => 
          notif._id === id ? { ...notif, leida: true } : notif
        )
      );
      
      // Decrementar contador
      setNoLeidas(prev => Math.max(0, prev - 1));
      
      return true;
    } catch (err) {
      log.error('Error al marcar notificación como leída:', err);
      return false;
    }
  }, []);

  /**
   * Marcar todas como leídas
   */
  const marcarTodasComoLeidas = useCallback(async () => {
    try {
      await notificacionService.marcarTodasComoLeidas();
      
      // Actualizar estado local
      setNotificaciones(prev => 
        prev.map(notif => ({ ...notif, leida: true }))
      );
      
      setNoLeidas(0);
      
      return true;
    } catch (err) {
      log.error('Error al marcar todas como leídas:', err);
      return false;
    }
  }, []);

  /**
   * Eliminar notificación
   */
  const eliminarNotificacion = useCallback(async (id) => {
    try {
      await notificacionService.eliminarNotificacion(id);
      
      // Actualizar estado local
      const notifEliminada = notificaciones.find(n => n._id === id);
      setNotificaciones(prev => prev.filter(notif => notif._id !== id));
      
      // Si no estaba leída, decrementar contador
      if (notifEliminada && !notifEliminada.leida) {
        setNoLeidas(prev => Math.max(0, prev - 1));
      }
      
      return true;
    } catch (err) {
      log.error('Error al eliminar notificación:', err);
      return false;
    }
  }, [notificaciones]);

  /**
   * Auto-fetch inicial y polling
   */
  useEffect(() => {
    if (autoFetch) {
      fetchNotificaciones();
      
      // Polling cada 30 segundos para actualizar contador
      const interval = setInterval(() => {
        fetchContadorNoLeidas();
      }, 30000); // 30 segundos
      
      return () => clearInterval(interval);
    }
  }, [autoFetch, fetchNotificaciones, fetchContadorNoLeidas]);

  return {
    notificaciones,
    noLeidas,
    loading,
    error,
    fetchNotificaciones,
    fetchContadorNoLeidas,
    marcarComoLeida,
    marcarTodasComoLeidas,
    eliminarNotificacion,
  };
};

export default useNotificaciones;
